// ── ReceiptCard ───────────────────────────────────────────────────────────────

import type { Receipt } from "@/application/money/types";
import { MoneyStatusBadge } from "./MoneyStatusBadge";

interface ReceiptCardProps {
  receipts: Receipt[];
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ReceiptCard({ receipts }: ReceiptCardProps) {
  return (
    <div className="bg-card rounded-lg border p-5">
      <h2 className="text-foreground mb-3 text-sm font-semibold">Receipts</h2>

      {receipts.length === 0 ? (
        <p className="text-subtle-foreground text-sm">No receipts recorded.</p>
      ) : (
        <div className="divide-y">
          {receipts.map((receipt) => (
            <div
              key={receipt.id}
              className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-x-4 py-3 text-sm"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-subtle-foreground text-xs uppercase">
                  Channel
                </span>
                <span className="text-foreground capitalize">
                  {receipt.type ?? "—"}
                </span>
              </div>

              <div className="flex flex-col gap-0.5">
                <span className="text-subtle-foreground text-xs uppercase">
                  Status
                </span>
                {receipt.status ? (
                  <MoneyStatusBadge status={receipt.status} />
                ) : (
                  <span className="text-subtle-foreground">—</span>
                )}
              </div>

              <div className="flex flex-col gap-0.5">
                <span className="text-subtle-foreground text-xs uppercase">
                  Sent to
                </span>
                <span className="text-foreground truncate">
                  {receipt.sentTo ?? "—"}
                </span>
              </div>

              <div className="flex flex-col gap-0.5">
                <span className="text-subtle-foreground text-xs uppercase">
                  Sent at
                </span>
                <span className="text-foreground">
                  {formatDateTime(receipt.sentAt)}
                </span>
              </div>

              {receipt.errorMessage && (
                <div className="col-span-4 mt-1">
                  <span className="text-xs text-red-600">
                    {receipt.errorMessage}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
